import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/** The "How to use" walkthrough, mirroring the Code RAG / Cash Flow /
 *  Commissions how-to-tab pattern. Grows as the feature does. */
export function HowToTab() {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>What this is</CardTitle>
          <CardDescription>
            A monitor for every inbound data feed, built so that{" "}
            <span className="font-medium">loud things are rare and real</span>. It watches
            the <em>business clock</em> (when transactions actually happened at the
            merchant), not the delivery clock — the old monitor watched deliveries and
            produced ~26 alerts a day, 92% of which were gone by the next morning. This one
            opens an incident roughly once a week, and it caught outages the old one missed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Data comes from the Snowflake mart, which loads in daily batches. The detector
            knows this: a day is only judged once the warehouse has actually been observed
            past it, so &quot;the mart hasn&apos;t loaded yet&quot; can never read as an
            outage — and a real outage can never hide behind it.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>The three places an integration breaks</CardTitle>
          <CardDescription>Each level has its own detector and its own tab.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-3 pl-5 text-sm">
            <li>
              <span className="font-medium">Integration (partner)</span> — Jiffy Lube&apos;s
              SFTP stops dropping files, or a whole feed dies. Caught by the delivery
              monitor (silent vs its declared cadence) and by volume anomalies on the
              business clock. A feed that stays dead becomes ONE standing{" "}
              <span className="font-medium">dormant</span> row, not a daily alarm.
            </li>
            <li>
              <span className="font-medium">Company (credentials)</span> — one company&apos;s
              credentials break and its data stops{" "}
              <em>while the integration keeps delivering for everyone else</em>. Statistics
              at the integration level can&apos;t see one company of a hundred, so this has
              its own detector: a company with a consistent sending history at zero
              arrivals for 3+ days, while its integration delivers, opens an incident on
              the Companies tab. The &quot;while its integration delivers&quot; part is the
              point — it separates <em>your credentials broke</em> from{" "}
              <em>the partner broke</em>.
            </li>
            <li>
              <span className="font-medium">Location (per-location files)</span> — some
              feeds deliver one flat file per location inside a batch; one file can stop
              while the rest continue. Same differential, one level down: a location quiet
              while its <em>company</em> still delivers. Location incidents are enabled
              per-feed in Admin (on for location-file feeds like jiffylube_sftp); every
              feed also gets the &quot;went quiet&quot; ledger on the Locations tab, which
              lists locations that stopped regardless of cause — churn, closure, or a
              break — for a human to eyeball.
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>The tabs, in the order you&apos;d reach for them</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-3 pl-5 text-sm">
            <li>
              <span className="font-medium">Overview</span> — the daily read. A short
              plain-language digest, one card per level with its open-incident count, and
              the alerts worklist. If this page is quiet, you&apos;re done.
            </li>
            <li>
              <span className="font-medium">Integrations</span> — the partner-level view:
              the health landscape (x = business flow, y = pipe health, bubble size =
              companies on the feed; big + bottom-left = look first), the delivery strip,
              and the dormant list. Click anything to open its drawer.
            </li>
            <li>
              <span className="font-medium">Companies / Locations</span> — what&apos;s
              broken <em>now</em> at that level, plus a browser: pick an integration (and a
              company, for locations) and walk its children with volume and trend. A trend
              ending in <span className="font-medium">→ 0</span> is the thing to click.
            </li>
            <li>
              <span className="font-medium">New &amp; Changed</span> — first appearances
              (new integrations, companies, locations, with first-event and first-arrival
              dates side by side) and material changes (went quiet, step change). Every row
              opens the entity&apos;s drawer.
            </li>
            <li>
              <span className="font-medium">Watchlist</span> — pin any integration,
              company, or location; watched keys are scored nightly and may open incidents
              at their own level. The list is shared company-wide — one list for everyone
              in CinchOps, with who-added-it on each entry.
            </li>
            <li>
              <span className="font-medium">Admin</span> — feed definitions (how each
              integration delivers: click a record to open its drawer, edit cadence,
              grains, and working notes there, and see the change log of how the
              record evolved), thresholds and alert budgets, the run log, and the
              manual run button. Notes you record feed the digest when that
              integration has trouble.
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reading and dispositioning an incident</CardTitle>
          <CardDescription>
            Your verdicts are not paperwork — they feed detection directly.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="list-disc space-y-3 pl-5 text-sm">
            <li>
              <span className="font-medium">The row</span>: severity (a solid red chip
              means an OPEN critical — everything else is tinted), level, who, days (the
              streak — &quot;dormant since&quot; when the feed is simply dead), and when it
              opened. The ✓ marks it <em>Expected</em> in one click; the flag opens the
              drawer with notes focused for a <em>Real issue</em>.
            </li>
            <li>
              <span className="font-medium">The drawer</span>: a plain-language delta line,
              the dual-clock chart (shaded = business events, dashed line = ingest
              arrivals — when they disagree, the story is delivery, not business), and the
              review panel with a cause picker.
            </li>
            <li>
              <span className="font-medium">Expected + a seasonal cause</span> (expected
              change / holiday-seasonal) closes the incident with a snooze — it resurfaces
              only if the numbers are still bad after the snooze ends. Your notes are kept
              forever and mined for structure; if the shortfall later doubles, an Expected
              verdict is marked superseded and the incident resurfaces rather than staying
              silently approved.
            </li>
            <li>
              <span className="font-medium">Early signals</span> (the quiet chips) are
              days still maturing that are tracking badly — confidence rises night over
              night as data lands. They never require action; when one confirms, the
              incident shows &quot;flagged early on …&quot;.
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How detection decides (plain language)</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc space-y-3 pl-5 text-sm">
            <li>
              Each feed has a learned <span className="font-medium">maturity age</span> —
              how many days until a business day&apos;s data has ~fully arrived (same-day
              for foreup, 2 days for grow, 5 for pestpac). Days are only judged at
              maturity, against the same weekday at the same age, so batch delivery and
              backfills can never fake a drop.
            </li>
            <li>
              Only <span className="font-medium">drops</span> alert, only when they&apos;re
              big enough to matter (a count floor, not just statistics), and only after{" "}
              <span className="font-medium">two consecutive bad days</span> — except a
              total collapse, which opens immediately as critical. US holidays are skipped.
            </li>
            <li>
              Consecutive bad days extend <span className="font-medium">one incident</span>{" "}
              (the Days column), and closing requires genuine recovery — a feed that
              shrinks and stabilizes can&apos;t flap.
            </li>
            <li>
              Each integration has an <span className="font-medium">alert budget</span>{" "}
              (default 4 per quarter). A monthly replay suggests threshold changes to stay
              inside it — suggestions only; a human applies them in Admin, and real
              (flagged) outages never count against the tuning.
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Coming next</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Automatic AI triage on every new incident (&quot;why this is probably
            happening&quot;) and an on-demand Claude deep review with a daily spend cap;
            later, a mappings explorer in Admin showing what each source system sends and
            where it lands in Cinch.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
